import { ArrowLeftIcon, BellAlertIcon } from '@heroicons/react/24/solid';
import successImage from '../../common/images/revamp/images/join-merchant-success.png';
import React, { useState } from 'react';
import { BottomSheet } from 'react-spring-bottom-sheet';
import Loader from '../../components/Loader';
import useNavigation from '../../hook/useNavigation';
import { axiosInstance } from '../../utils/helper';
import { useSelector } from 'react-redux';

const InputField = ({
    label,
    name,
    value,
    onChange,
    placeholder,
    type = "text",
    error
}) => {
    return (
        <div className="mb-4">
            <label className="block font-bold text-gray-800 mb-2">
                {label}
                <span className="text-red-500 ml-0.5">*</span>
            </label>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                className={`w-full !mb-1 border-2 border-solid !bg-white rounded-lg px-4 py-3 text-sm placeholder:text-xs placeholder-[#909090] focus:outline-none placeholder:font-semibold transition-colors
            ${error
                        ? 'ring-1 ring-red-500 ring:border-red-600 focus:ring-1 focus:ring-red-500'
                        : 'ring-1 ring-[#D3D3D3] focus:border-blue-600 focus:ring-1 focus:ring-blue-600'
                    }`}
            />
            {error && (
                <div className="flex items-center text-red-500 text-xs">
                    <span>{error}</span>
                </div>
            )}
        </div>
    );
};

const AlertBottomSheet = ({
    open,
    setOpen,
    type = 'success',
    title,
    message,
    buttonMessage,
    buttonOnClick,
}) => {
    return (
        <BottomSheet
            open={open}
            onDismiss={() => setOpen(false)}
            className="z-50"
        >
            <div className="px-6 pb-8 pt-2">
                <div className="flex justify-center mb-6">
                    {type === 'success' ? (
                        <img src={successImage} alt="success" />
                    ) : (
                        <BellAlertIcon className="h-12 w-12 text-red-500" />
                    )}
                </div>

                <div className="text-center mb-12">
                    <h2 className="text-xl font-bold text-gray-800 mb-2">
                        {title}
                    </h2>
                    <p className="text-gray-500 text-sm">
                        {message}
                    </p>
                </div>

                <button
                    className="w-full py-3.5 rounded-xl border-2 border-blue-500 text-blue-600 font-bold text-base hover:bg-blue-50 active:bg-blue-100 transition-colors duration-200"
                    onClick={buttonOnClick}
                >
                    {buttonMessage}
                </button>
            </div>
        </BottomSheet>
    );
};

const JoinMerchant = () => {
    const user = useSelector((store) => store?.user || {});
    const navigate = useNavigation();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        companyName: '',
        name: '',
        email: '',
        phone: '',
        outlets: '',
        province: '',
        city: '',
        subdistrict: ''
    });

    const validate = () => {
        let tempErrors = {};
        let phone = formData?.phone?.replace(/\D/g, '');

        if (!formData?.companyName) tempErrors.companyName = "Nama perusahaan wajib diisi";
        if (!formData?.name) tempErrors.name = "Nama PIC wajib diisi";
        if (!formData?.province) tempErrors.province = "Provinsi wajib diisi";
        if (!formData?.city) tempErrors.city = "Kota wajib diisi";
        if (!formData?.subdistrict) tempErrors.subdistrict = "Kecamatan wajib diisi";

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData?.email) {
            tempErrors.email = "Email wajib diisi";
        } else if (!emailRegex.test(formData?.email)) {
            tempErrors.email = "Format email tidak valid";
        }

        if (phone.startsWith('0')) {
            phone = '8' + phone.slice(1);

            setFormData(prev => ({
                ...prev,
                phone
            }))
        }

        if (phone.length === 0) {
            tempErrors.phone = "Nomor WhatsApp wajib diisi";
        } else if (phone.length < 9) {
            tempErrors.phone = "Nomor WhatsApp wajib diisi minimal 9 angka";
        } else if (phone.length > 15) {
            tempErrors.phone = "Nomor WhatsApp wajib diisi maksimal 15 angka";
        }

        if (!formData?.outlets) {
            tempErrors.outlets = "Jumlah outlet wajib diisi";
        } else if (parseInt(formData?.outlets) < 1) {
            tempErrors.outlets = "Minimal 1 outlet";
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        if (errors[name]) {
            setErrors({ ...errors, [name]: null });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validate()) {
            setLoading(true);

            try {
                const payload = new FormData();

                payload.append('user_id', user?.customerID || '');
                payload.append('company_name', formData?.companyName || '');
                payload.append('customer_name', formData?.name || '');
                payload.append('email', formData?.email || '');
                payload.append('phone', formData?.phone || '');
                payload.append('outlets', parseInt(formData?.outlets || 0)); 
                payload.append('province', formData?.province || '');
                payload.append('city', formData?.city || '');
                payload.append('subdistrict', formData?.subdistrict || '');

                const response = await axiosInstance.post('joinmerchant/add', payload, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });

                if (response?.status == 200) {
                    setErrors({});
                    setFormData({
                        companyName: '',
                        name: '',
                        email: '',
                        phone: '',
                        outlets: '',
                        province: '',
                        city: '',
                        subdistrict: ''
                    });
                } else {
                    setErrors({
                        post: {
                            status: "error",
                            message: response?.data?.message || "Something went wrong. Please try again later."
                        }
                    });
                }
            } catch (error) {
                console.log('error', error);
                setErrors({
                    post: {
                        status: "error",
                        message: error.response?.data?.message || "Something went wrong. Please try again later."
                    }
                });
            } finally {
                setLoading(false);
                setOpen(true); 
            }
        }
    };

    return (
        <>  
            {loading && <Loader overlay />}
            <div className="min-h-screen font-sans flex justify-center">
                {open && (
                    <AlertBottomSheet
                        open={open}
                        setOpen={setOpen}
                        type={errors?.post ? "error" : "success"}
                        title={errors?.post?.status == "error" ? "Error" : "Thank you for submit your data"}
                        message={errors?.post?.message ? errors?.post?.message : "Our team will contact you soon"}
                        buttonMessage={errors?.post?.status == "error" ? "Close" : "Back to Home"}
                        buttonOnClick={errors?.post?.status == "error" ? () => setOpen(false) : () => navigate.push('/')}
                    />
                )} 
                <div className="w-full max-w-md p-5 pb-10 min-h-screen">
                    <header className="mb-6">
                        <button 
                            onClick={() => navigate.push('/')}
                            className="mb-5 border-none"
                        >
                            <ArrowLeftIcon className="h-8 w-8" />
                        </button>

                        <h1 className="text-xl font-bold text-gray-900">
                            Gabung Sebagai Merchant
                        </h1>
                        <p className="text-gray-500 text-sm mt-1 text-[#646464] font-bold">
                            Please enter your details here
                        </p>
                    </header>

                    <form onSubmit={handleSubmit} className="space-y-1">
                        <div className='mb-8'>
                            <InputField
                                label="Nama Perusahaan/Brand"
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleChange}
                                placeholder="Masukan nama perusahaan anda"
                                error={errors.companyName}
                            />

                            <InputField
                                label="Nama PIC"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Masukan nama anda"
                                error={errors.name}
                            />

                            <InputField
                                label="Email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Masukan email anda"
                                error={errors.email}
                            />

                            <InputField
                                label="Nomor Whatsapp"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="Masukan nomor whatsapp anda"
                                error={errors.phone}
                            />

                            <InputField
                                label="Jumlah Outlet"
                                name="outlets"
                                value={formData.outlets}
                                onChange={handleChange}
                                placeholder="Masukan jumlah outlet anda"
                                error={errors.outlets}
                            />

                            <InputField
                                label="Provinsi"
                                name="province"
                                value={formData.province}
                                onChange={handleChange}
                                placeholder="Masukan provinsi perusahaan/brand anda"
                                error={errors.province}
                            />

                            <InputField
                                label="Kota"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                placeholder="Masukan kota perusahaan/brand anda"
                                error={errors.city}
                            />

                            <InputField
                                label="Kecamatan"
                                name="subdistrict"
                                value={formData.subdistrict}
                                onChange={handleChange}
                                placeholder="Masukan kecamatan perusahaan/brand anda"
                                error={errors.subdistrict}
                            />
                        </div>

                        <div className="">
                            <button
                                type="submit"
                                className="w-full bg-[#a84198] text-white font-bold py-3.5 rounded-lg shadow-sm border-none"
                                disabled={loading}
                            >
                                Submit
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default JoinMerchant;